// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 15-database-schema.md#feedback
// @greenlight-spec: 16-api-conventions.md
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { FEEDBACK_CAPS, FEEDBACK_TRIAGE_STATUSES, FeedbackBodySchema, FeedbackCategorySchema, FeedbackSourceSchema, FeedbackStatusSchema, FeedbackSurfaceSchema, FeedbackTitleSchema, PaginationQuerySchema, UserFeedbackContextSchema, paginated, } from '@greenlight/shared';
import { errorResponses } from './common.js';
const c = initContract();
const isoDateTime = () => z.string().datetime({ offset: true });
// The triage-list envelope: everything but the (untrusted, potentially large)
// body, which loads via `get` on click — the same body-omitted-in-list pattern
// as knowledge entries. `context` is the stored source-specific envelope; the UI
// reads it by `source` (agent vs user), so it rides as an open record here.
export const FeedbackListItemSchema = z.object({
    id: z.string().uuid(),
    source: FeedbackSourceSchema,
    surface: FeedbackSurfaceSchema,
    category: FeedbackCategorySchema,
    title: z.string(),
    app_id: z.string().uuid().nullable(),
    app_slug: z.string().nullable(),
    submitted_by: z.string().uuid(),
    submitted_by_email: z.string().nullable(),
    submitted_by_name: z.string().nullable(),
    status: FeedbackStatusSchema,
    sent_at: isoDateTime().nullable(),
    context: z.record(z.unknown()),
    created_at: isoDateTime(),
});
export const FeedbackItemSchema = FeedbackListItemSchema.extend({
    body: z.string(),
    updated_at: isoDateTime(),
});
// Dashboard submit body. `source`/`surface` are never accepted here — the server
// derives them from the channel (this route is always source='user',
// surface='dashboard'). `context` is the user allowlist (route/UA/viewport).
export const SubmitFeedbackSchema = z.object({
    category: FeedbackCategorySchema,
    title: FeedbackTitleSchema,
    body: FeedbackBodySchema,
    app_id: z.string().uuid().optional(),
    context: UserFeedbackContextSchema.optional(),
});
export const SubmitFeedbackResultSchema = z.object({
    feedback_id: z.string().uuid(),
    status: FeedbackStatusSchema,
});
export const FeedbackListQuerySchema = PaginationQuerySchema.extend({
    source: FeedbackSourceSchema.optional(),
    status: FeedbackStatusSchema.optional(),
    category: FeedbackCategorySchema.optional(),
    app_id: z.string().uuid().optional(),
});
// Bulk triage: mark the selected items read/unread/archived. Capped so one call
// cannot mutate an unbounded set; the server writes one audit event for the batch.
// `sent` is not accepted here — it records a hand-off that actually happened, so
// only the share path writes it. Setting it by hand would claim Shift has
// something it does not.
export const UpdateFeedbackStatusSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(FEEDBACK_CAPS.maxBulkIds),
    status: z.enum(FEEDBACK_TRIAGE_STATUSES),
});
/**
 * Hand selected items to Shift. `via: 'fleet'` makes the server POST them to the
 * fleet ingest and mark them `sent` only on a 2xx; `via: 'download'` records that
 * the org-admin already downloaded them for hand-off, which the client is the
 * only party that can know.
 */
export const ShareFeedbackSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(FEEDBACK_CAPS.maxBulkIds),
    via: z.enum(['fleet', 'download']),
});
/**
 * The Markdown hand-off document, rendered server-side because only the server
 * knows the org name and installation id the header carries — and because the
 * shape is a privacy contract, not a presentation choice: it must exclude the
 * submitter identity the org-admin's own table still shows.
 */
export const FeedbackExportSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(FEEDBACK_CAPS.maxBulkIds),
});
export const FeedbackExportResultSchema = z.object({
    markdown: z.string(),
    item_count: z.number().int().nonnegative(),
});
export const ShareFeedbackResultSchema = z.object({
    shared: z.number().int().nonnegative(),
    /** Set when nothing was handed over, so the UI can say why. */
    reason: z.enum(['not_enrolled', 'nothing_to_share', 'rejected', 'unavailable']).optional(),
});
export const FeedbackStatusUpdateResultSchema = z.object({
    updated: z.number().int().nonnegative(),
});
export const FeedbackSummarySchema = z.object({
    unread_count: z.number().int().nonnegative(),
});
// One failed pipeline run on the /feedback Pipeline failures view. Only the
// failed checks' names and one-line summaries ride the list; check detail and
// log tails load through the app's run read when a row is opened.
export const PipelineFailureListItemSchema = z.object({
    run_id: z.string().uuid(),
    app_id: z.string().uuid(),
    app_slug: z.string(),
    app_name: z.string(),
    branch: z.string(),
    pull_request_number: z.number().int().positive().nullable(),
    commit_sha: z.string(),
    failed_checks: z.array(z.object({
        check_name: z.string(),
        error_summary: z.string().nullable(),
    })),
    triggered_at: isoDateTime(),
    completed_at: isoDateTime().nullable(),
    /** The run's row in the failure-log ledger; `error_code` says why it is still sending or gave up. */
    delivery: z.object({
        status: z.enum(['not_sent', 'pending', 'sent', 'gave_up']),
        sent_at: isoDateTime().nullable(),
        error_code: z.string().nullable(),
    }),
});
// One captured app crash on the /feedback App crashes view. The log tail loads
// with the detail read; `delivery` is the crash's row in the failure-log ledger.
export const AppCrashListItemSchema = z.object({
    id: z.string().uuid(),
    app_id: z.string().uuid(),
    app_slug: z.string(),
    app_name: z.string(),
    reason: z.string(),
    exit_code: z.number().int().nullable(),
    image: z.string(),
    occurred_at: isoDateTime(),
    last_occurred_at: isoDateTime(),
    occurrence_count: z.number().int().positive(),
    has_log: z.boolean(),
    log_byte_length: z.number().int().nonnegative(),
    log_truncated: z.boolean(),
    send_requested_at: isoDateTime().nullable(),
    delivery: z.object({
        status: z.enum(['not_sent', 'pending', 'sent', 'gave_up']),
        sent_at: isoDateTime().nullable(),
    }),
});
export const AppCrashDetailSchema = AppCrashListItemSchema.extend({
    log_tail: z.string().nullable(),
    log_unavailable_reason: z.string().nullable(),
});
export const AppCrashListQuerySchema = PaginationQuerySchema.extend({
    app_id: z.string().uuid().optional(),
});
export const ShareAppCrashesSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(100),
});
export const ShareAppCrashesResultSchema = z.object({
    queued: z.number().int().nonnegative(),
    already_sent: z.number().int().nonnegative(),
});
// One app health alert on the /feedback Health alerts view: an app tripping into
// `degraded`, listed whether or not a notification went out. `notified` is what
// was queued when it fired; delivery outcomes are in the audit log.
export const HealthAlertListItemSchema = z.object({
    id: z.string().uuid(),
    app_id: z.string().uuid(),
    app_slug: z.string(),
    app_name: z.string(),
    alert: z.enum(['crash_loop']),
    restarts: z.number().int().nonnegative(),
    window_minutes: z.number().int().positive(),
    notified: z.object({ email: z.boolean(), slack: z.boolean() }),
    occurred_at: isoDateTime(),
    send_requested_at: isoDateTime().nullable(),
    delivery: z.object({
        status: z.enum(['not_sent', 'pending', 'sent', 'gave_up']),
        sent_at: isoDateTime().nullable(),
    }),
});
export const ShareHealthAlertsSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(100),
});
export const ShareHealthAlertsResultSchema = ShareAppCrashesResultSchema;
const FeedbackIdParams = z.object({ id: z.string().uuid() });
export const feedbackContract = c.router({
    submit: {
        method: 'POST',
        path: '/api/feedback',
        body: SubmitFeedbackSchema,
        responses: {
            200: SubmitFeedbackResultSchema,
            ...errorResponses,
        },
        summary: 'Submit dashboard user feedback (source derived server-side; any signed-in role).',
    },
    summary: {
        method: 'GET',
        path: '/api/feedback/summary',
        responses: {
            200: FeedbackSummarySchema,
            ...errorResponses,
        },
        summary: 'Unread feedback count for the triage indicator. Org-admin only.',
    },
    list: {
        method: 'GET',
        path: '/api/feedback',
        query: FeedbackListQuerySchema,
        responses: {
            200: paginated(FeedbackListItemSchema),
            ...errorResponses,
        },
        summary: 'List feedback newest-first (cursor-paginated), filtered by source/status/category/app. Org-admin only.',
    },
    pipelineFailures: {
        method: 'GET',
        path: '/api/feedback/pipeline-failures',
        query: PaginationQuerySchema,
        responses: {
            200: paginated(PipelineFailureListItemSchema),
            ...errorResponses,
        },
        summary: "List the org's failed pipeline runs newest-first (cursor-paginated). Org-admin only.",
    },
    appCrashes: {
        method: 'GET',
        path: '/api/feedback/app-crashes',
        query: AppCrashListQuerySchema,
        responses: {
            200: paginated(AppCrashListItemSchema),
            ...errorResponses,
        },
        summary: "List the org's captured app crashes newest-first (cursor-paginated). Org-admin only.",
    },
    appCrash: {
        method: 'GET',
        path: '/api/feedback/app-crashes/:id',
        pathParams: FeedbackIdParams,
        responses: {
            200: AppCrashDetailSchema,
            ...errorResponses,
        },
        summary: 'Read one captured app crash with its redacted log tail. Org-admin only.',
    },
    shareAppCrashes: {
        method: 'POST',
        path: '/api/feedback/app-crashes/share',
        body: ShareAppCrashesSchema,
        responses: {
            200: ShareAppCrashesResultSchema,
            ...errorResponses,
        },
        summary: 'Send the chosen app crashes to Shift, whatever the crash-log sharing setting says (ADR-0069). Audited; org-admin only.',
    },
    healthAlerts: {
        method: 'GET',
        path: '/api/feedback/health-alerts',
        query: PaginationQuerySchema,
        responses: {
            200: paginated(HealthAlertListItemSchema),
            ...errorResponses,
        },
        summary: "List the org's app health alerts newest-first (cursor-paginated). Org-admin only.",
    },
    shareHealthAlerts: {
        method: 'POST',
        path: '/api/feedback/health-alerts/share',
        body: ShareHealthAlertsSchema,
        responses: {
            200: ShareHealthAlertsResultSchema,
            ...errorResponses,
        },
        summary: 'Send the chosen app health alerts to Shift (ADR-0069). Audited; org-admin only.',
    },
    get: {
        method: 'GET',
        path: '/api/feedback/:id',
        pathParams: FeedbackIdParams,
        responses: {
            200: FeedbackItemSchema,
            ...errorResponses,
        },
        summary: 'Fetch one feedback item including its body. Org-admin only.',
    },
    exportMarkdown: {
        method: 'POST',
        path: '/api/feedback/export',
        body: FeedbackExportSchema,
        responses: {
            200: FeedbackExportResultSchema,
            ...errorResponses,
        },
        summary: 'Render selected feedback as the Shift hand-off Markdown (no submitter identity). Org-admin only.',
    },
    share: {
        method: 'POST',
        path: '/api/feedback/share',
        body: ShareFeedbackSchema,
        responses: {
            200: ShareFeedbackResultSchema,
            ...errorResponses,
        },
        summary: 'Hand selected feedback to Shift (fleet ingest or a completed download). Org-admin only.',
    },
    updateStatus: {
        method: 'PATCH',
        path: '/api/feedback/status',
        body: UpdateFeedbackStatusSchema,
        responses: {
            200: FeedbackStatusUpdateResultSchema,
            ...errorResponses,
        },
        summary: 'Bulk mark selected feedback read/unread/archived. Org-admin only.',
    },
});
//# sourceMappingURL=feedback.js.map